import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/authStore';
import { toast } from 'sonner';
import { apiClient, useApiQuery } from '../../../api/client';
import type { DuplicateGroup } from '../../transactions/api/useDuplicates';

interface DuplicatesResponse {
  groups: DuplicateGroup[];
  total: number;
}

interface NotificationContextValue {
  /** Total actionable items: notifications + duplicates + proposed recurring matches */
  actionCount: number;
  /** Count from the notifications API only */
  notificationActionCount: number;
  /** Duplicate transaction groups pending resolution */
  duplicateCount: number;
  /** Proposed recurring payment matches pending confirmation */
  proposedMatchCount: number;
  isConnected: boolean;
}

const NotificationContext = createContext<NotificationContextValue>({
  actionCount: 0,
  notificationActionCount: 0,
  duplicateCount: 0,
  proposedMatchCount: 0,
  isConnected: false,
});

export function useNotificationContext() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notificationActionCount, setNotificationActionCount] = useState(0);
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Duplicate groups from the duplicates API (only when authenticated)
  const { data: duplicateData } = useApiQuery<DuplicatesResponse>(
    ['transactions', 'duplicates'],
    '/api/transactions/duplicates',
    { enabled: isAuthenticated, staleTime: 5 * 60 * 1000 },
  );
  const duplicateCount = duplicateData?.total ?? 0;

  // Proposed recurring matches (only when authenticated)
  const { data: proposedData } = useApiQuery<{ grouped: unknown[]; flat: unknown[] }>(
    ['recurring', 'proposed'],
    '/api/recurring/proposed',
    { enabled: isAuthenticated, staleTime: 2 * 60 * 1000 },
  );
  const proposedMatchCount = proposedData?.grouped?.length ?? 0;

  // Total action count across all sources
  const actionCount = notificationActionCount + duplicateCount + proposedMatchCount;

  useEffect(() => {
    if (!isAuthenticated || !token) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setNotificationActionCount(0);
      return;
    }

    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const socket = io(`${apiBase}/notifications`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 5000,
      reconnectionAttempts: 5,
      timeout: 10000,
      autoConnect: false,
    });

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));
    socket.on('connect_error', () => setIsConnected(false));

    // Delay connect to avoid race with auth state settling
    const connectTimer = setTimeout(() => socket.connect(), 500);

    socket.on('notification:new', (notification: any) => {
      // Invalidate notifications query cache
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Update notification-specific action count
      if (notification.priority === 'ACTION') {
        setNotificationActionCount((c) => c + 1);
      }

      // Show toast for ACTION and ALERT
      if (notification.priority === 'ACTION') {
        toast.warning(notification.title, { description: notification.body, duration: 8000 });
      } else if (notification.priority === 'ALERT') {
        toast.info(notification.title, { description: notification.body, duration: 5000 });
      }
    });

    socket.on('notification:resolved', () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setNotificationActionCount((c) => Math.max(0, c - 1));
    });

    // Live refresh when a recurring payment is confirmed or rejected
    socket.on('recurring:payment_resolved', (payload: { entityId: string; action: string }) => {
      queryClient.invalidateQueries({ queryKey: ['recurring', 'proposed'] });
      queryClient.invalidateQueries({ queryKey: ['recurring'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'cash-flow'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'duplicates'] });
      if (payload?.entityId) {
        queryClient.invalidateQueries({ queryKey: ['recurring', payload.entityId, 'payments'] });
      }
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    socketRef.current = socket;

    return () => {
      clearTimeout(connectTimer);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, token, queryClient]);

  // Fetch initial notification action count on mount (uses apiClient for token refresh)
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    apiClient
      .get<{ success: boolean; data: { count: number } }>('/api/notifications/action-count')
      .then((r) => {
        if (r.data.success) setNotificationActionCount(r.data.data.count);
      })
      .catch(() => {});
  }, [isAuthenticated, token]);

  return (
    <NotificationContext.Provider
      value={{
        actionCount,
        notificationActionCount,
        duplicateCount,
        proposedMatchCount,
        isConnected,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
